// Tool: Odpočty času — správa countdown GIFů (à la MailTimers).

import { Toast } from '@/components/Toast';
import { ApiError, apiFetch } from '@/lib/apiFetch';
import { fmtDateTime } from '@/lib/format';
import { hasModule } from '@/lib/modules';
import type { MeResponse } from '@sklad-sync/shared';
import { type FormEvent, useCallback, useEffect, useState } from 'react';

type Props = { user: MeResponse };

type CountdownDesign = {
  font: 'open-sans' | 'montserrat' | 'roboto';
  textColor: string;
  boxColor: string;
  bgColor: string;
  labelColor: string;
  radius: number;
  transparentBg: boolean;
  paddingX: number;
};

const DEFAULT_DESIGN: CountdownDesign = {
  font: 'open-sans',
  textColor: '#ffffff',
  boxColor: '#16243a',
  bgColor: '#ffffff',
  labelColor: '#64748b',
  radius: 14,
  transparentBg: false,
  paddingX: 0,
};

const FONT_OPTIONS: Array<{ value: CountdownDesign['font']; label: string }> = [
  { value: 'open-sans', label: 'Open Sans' },
  { value: 'montserrat', label: 'Montserrat' },
  { value: 'roboto', label: 'Roboto' },
];

type Countdown = {
  id: string;
  name: string;
  monthly: boolean;
  steps: number[]; // unix ms, vzestupně
  design: CountdownDesign;
  created_at: number;
  created_by: string | null;
};

function publicUrl(id: string): string {
  return `${window.location.origin}/t/${id}.gif`;
}

function embedCode(id: string): string {
  return `<script src="${window.location.origin}/countdown.js" defer></script>\n<div data-vm-countdown="${id}"></div>`;
}

// unix ms → hodnota pro <input type="datetime-local"> (lokální čas)
function msToLocalInput(ms: number): string {
  const d = new Date(ms);
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`;
}

export function CountdownsPage({ user }: Props) {
  const isAdmin = hasModule(user.modules, 'odpocty');
  const [countdowns, setCountdowns] = useState<Countdown[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState<Countdown | 'new' | null>(null);
  const [tick, setTick] = useState(0);
  const [toast, setToast] = useState<{ msg: string; kind: 'success' | 'error' } | null>(null);

  const load = useCallback(async () => {
    try {
      const data = await apiFetch<{ countdowns: Countdown[] }>('/api/countdowns');
      setCountdowns(data.countdowns);
      setTick((t) => t + 1);
    } catch (e: unknown) {
      setError(e instanceof ApiError ? e.message : 'Chyba načítání');
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  // GIF má 60 snímků (60 s) a pak zamrzne — na admin stránce ho proto
  // každých 55 s znovu načteme, ať náhled vypadá živě.
  useEffect(() => {
    const iv = setInterval(() => setTick((t) => t + 1), 55_000);
    return () => clearInterval(iv);
  }, []);

  async function remove(cd: Countdown) {
    if (!confirm(`Smazat odpočet „${cd.name}"? Veřejná GIF URL přestane fungovat.`)) {
      return;
    }
    try {
      await apiFetch(`/api/countdowns/${cd.id}`, { method: 'DELETE' });
      setToast({ msg: 'Odpočet smazán', kind: 'success' });
      await load();
    } catch (e: unknown) {
      setToast({
        msg: e instanceof ApiError ? e.message : 'Smazání selhalo',
        kind: 'error',
      });
    }
  }

  async function copyText(text: string) {
    try {
      await navigator.clipboard.writeText(text);
      setToast({ msg: 'Zkopírováno do schránky', kind: 'success' });
    } catch {
      setToast({ msg: 'Kopírování selhalo', kind: 'error' });
    }
  }

  return (
    <>
      <div className="mb-6 flex items-end justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-text">Odpočty času</h1>
          <p className="text-sm text-text-secondary mt-0.5 max-w-2xl">
            Countdown GIF do emailů — vlož veřejnou URL jako obrázek a v emailu se zobrazí živý
            odpočet (běží ~60 s po otevření).
          </p>
        </div>
        {isAdmin && (
          <button
            type="button"
            onClick={() => setEditing('new')}
            className="bg-action hover:bg-action-dark text-white text-sm font-semibold rounded-lg px-6 py-2.5 transition-colors cursor-pointer"
          >
            Přidat odpočet
          </button>
        )}
      </div>

      {error && (
        <div className="text-sm text-danger bg-danger-light border border-danger/30 rounded-lg px-4 py-3 mb-4">
          {error}
        </div>
      )}

      {countdowns === null && <div className="text-text-muted text-sm">Načítám…</div>}
      {countdowns && countdowns.length === 0 && (
        <div className="bg-surface rounded-xl shadow-card p-8 text-center text-text-muted text-sm">
          Žádné odpočty zatím nejsou. Klikni na „Přidat odpočet".
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        {countdowns?.map((cd) => (
          <div key={cd.id} className="bg-surface rounded-xl shadow-card p-5">
            <div className="flex items-start justify-between gap-3 mb-3">
              <div className="min-w-0">
                <h2 className="text-base font-bold text-text truncate">{cd.name}</h2>
                <div className="text-xs text-text-muted mt-0.5">
                  {cd.monthly ? (
                    <span className="text-primary font-medium">Měsíční (do konce měsíce)</span>
                  ) : (
                    <span>{cd.steps.length} termínů</span>
                  )}
                  {' · vytvořeno '}
                  {fmtDateTime(cd.created_at)}
                </div>
              </div>
              {isAdmin && (
                <div className="flex items-center gap-1.5 flex-shrink-0">
                  <button
                    type="button"
                    onClick={() => setEditing(cd)}
                    className="text-xs font-medium text-text-secondary hover:text-text bg-bg hover:bg-surface-hover border border-border rounded px-2.5 py-1 cursor-pointer transition-colors"
                  >
                    Upravit
                  </button>
                  <button
                    type="button"
                    onClick={() => remove(cd)}
                    className="text-xs font-medium text-danger hover:bg-danger-light border border-danger/30 rounded px-2.5 py-1 cursor-pointer transition-colors"
                  >
                    Smazat
                  </button>
                </div>
              )}
            </div>

            {/* Náhled GIFu */}
            <img
              src={`${publicUrl(cd.id)}?t=${tick}`}
              alt={`Odpočet ${cd.name}`}
              className="w-full max-w-md rounded-lg border border-border bg-bg"
            />

            {/* GIF do emailů */}
            <div className="mt-3">
              <div className="text-[11px] font-semibold text-text-secondary mb-1">
                GIF do emailů
              </div>
              <div className="flex items-center gap-2">
                <code className="flex-1 min-w-0 text-[11px] text-text-secondary bg-bg border border-border rounded px-2.5 py-1.5 truncate font-mono">
                  {publicUrl(cd.id)}
                </code>
                <button
                  type="button"
                  onClick={() => copyText(publicUrl(cd.id))}
                  className="text-xs font-medium text-text-secondary hover:text-text bg-bg hover:bg-surface-hover border border-border rounded px-3 py-1.5 cursor-pointer transition-colors flex-shrink-0"
                >
                  Kopírovat
                </button>
              </div>
            </div>

            {/* Widget na web (přesný, tiká donekonečna) */}
            <div className="mt-3">
              <div className="text-[11px] font-semibold text-text-secondary mb-1">
                Widget na web (detail produktu apod.)
              </div>
              <div className="flex items-start gap-2">
                <pre className="flex-1 min-w-0 text-[11px] text-text-secondary bg-bg border border-border rounded px-2.5 py-1.5 overflow-x-auto font-mono">
                  {embedCode(cd.id)}
                </pre>
                <button
                  type="button"
                  onClick={() => copyText(embedCode(cd.id))}
                  className="text-xs font-medium text-text-secondary hover:text-text bg-bg hover:bg-surface-hover border border-border rounded px-3 py-1.5 cursor-pointer transition-colors flex-shrink-0"
                >
                  Kopírovat
                </button>
              </div>
            </div>

            {!cd.monthly && cd.steps.length > 0 && (
              <div className="mt-3 text-[11px] text-text-muted">
                Termíny:{' '}
                {cd.steps.map((s, i) => (
                  <span key={s}>
                    {i > 0 && ' → '}
                    {fmtDateTime(s)}
                  </span>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>

      {editing && (
        <CountdownModal
          countdown={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={(msg) => {
            setToast({ msg, kind: 'success' });
            setEditing(null);
            load();
          }}
          onError={(msg) => setToast({ msg, kind: 'error' })}
        />
      )}

      {toast && <Toast message={toast.msg} kind={toast.kind} onClose={() => setToast(null)} />}
    </>
  );
}

// ─── Modal pro vytvoření / editaci ──────────────────────────────────────

type ModalProps = {
  countdown: Countdown | null;
  onClose: () => void;
  onSaved: (msg: string) => void;
  onError: (msg: string) => void;
};

function CountdownModal({ countdown, onClose, onSaved, onError }: ModalProps) {
  const isEdit = countdown !== null;
  const [name, setName] = useState(countdown?.name ?? '');
  const [monthly, setMonthly] = useState<boolean>(countdown?.monthly ?? false);
  // Termíny jako stringy pro <input type="datetime-local">.
  const [steps, setSteps] = useState<string[]>(
    countdown && countdown.steps.length > 0 ? countdown.steps.map(msToLocalInput) : [''],
  );
  const [design, setDesign] = useState<CountdownDesign>(countdown?.design ?? DEFAULT_DESIGN);
  const [saving, setSaving] = useState(false);

  function setDesignField<K extends keyof CountdownDesign>(key: K, value: CountdownDesign[K]) {
    setDesign((prev) => ({ ...prev, [key]: value }));
  }

  // Živý náhled — debounceovaně překresluje GIF při změně designu.
  const [previewSrc, setPreviewSrc] = useState('');
  useEffect(() => {
    const colorsOk = [design.textColor, design.boxColor, design.bgColor, design.labelColor].every(
      (cl) => /^#[0-9a-fA-F]{6}$/.test(cl),
    );
    if (!colorsOk) return;
    const timer = setTimeout(() => {
      const p = new URLSearchParams({
        font: design.font,
        textColor: design.textColor,
        boxColor: design.boxColor,
        bgColor: design.bgColor,
        labelColor: design.labelColor,
        radius: String(design.radius),
        transparentBg: String(design.transparentBg),
        paddingX: String(design.paddingX),
        v: String(Date.now()),
      });
      setPreviewSrc(`/api/countdowns/preview.gif?${p.toString()}`);
    }, 400);
    return () => clearTimeout(timer);
  }, [design]);

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose();
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  function setStep(idx: number, value: string) {
    setSteps((prev) => prev.map((s, i) => (i === idx ? value : s)));
  }
  function addStep() {
    setSteps((prev) => [...prev, '']);
  }
  function removeStep(idx: number) {
    setSteps((prev) => (prev.length > 1 ? prev.filter((_, i) => i !== idx) : prev));
  }

  async function submit(e: FormEvent) {
    e.preventDefault();
    const stepMs = monthly
      ? []
      : steps
          .map((s) => s.trim())
          .filter(Boolean)
          .map((s) => new Date(s).getTime())
          .filter((n) => Number.isFinite(n));
    if (!monthly && stepMs.length === 0) {
      onError('Přidej alespoň jeden termín, nebo zapni měsíční režim.');
      return;
    }
    setSaving(true);
    try {
      const payload = { name: name.trim(), monthly, steps: stepMs, design };
      if (isEdit) {
        await apiFetch(`/api/countdowns/${countdown.id}`, {
          method: 'PATCH',
          body: payload,
        });
      } else {
        await apiFetch('/api/countdowns', { method: 'POST', body: payload });
      }
      onSaved(isEdit ? 'Odpočet upraven' : 'Odpočet vytvořen');
    } catch (err: unknown) {
      onError(err instanceof ApiError ? err.describe() : 'Uložení selhalo');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div
      className="fixed inset-0 z-40 flex items-start justify-center bg-black/40 p-4 overflow-y-auto"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="bg-surface rounded-xl shadow-card-hover w-full max-w-lg my-8">
        <form onSubmit={submit} className="p-6 space-y-4">
          <h2 className="text-lg font-bold text-text">
            {isEdit ? 'Upravit odpočet' : 'Nový odpočet'}
          </h2>

          <div>
            <label
              className="text-sm font-medium text-text-secondary block mb-1.5"
              htmlFor="cd_name"
            >
              Název <span className="text-danger">*</span>
            </label>
            <input
              id="cd_name"
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              autoFocus
              placeholder="Např. Letní výprodej"
              className="w-full text-sm text-text placeholder:text-text-muted bg-bg border border-border rounded-lg px-3.5 py-2.5 outline-none focus:border-primary focus:ring-2 focus:ring-primary-100 transition-all"
            />
          </div>

          <div>
            <label className="flex items-center gap-2 cursor-pointer select-none">
              <input
                type="checkbox"
                checked={monthly}
                onChange={(e) => setMonthly(e.target.checked)}
                className="w-4 h-4 rounded border-border accent-primary cursor-pointer"
              />
              <span className="text-sm font-medium text-text">Měsíční opakovatelný odpočet</span>
            </label>
            <p className="text-xs text-text-muted mt-1 ml-6">
              Odpočítává vždy do konce aktuálního měsíce (poslední den 23:59:59). Po skončení měsíce
              automaticky naskočí další.
            </p>
          </div>

          {!monthly && (
            <div>
              <span className="text-sm font-medium text-text-secondary block mb-1.5">Termíny</span>
              <div className="space-y-2">
                {steps.map((s, idx) => (
                  // biome-ignore lint/suspicious/noArrayIndexKey: řádky jsou poziční
                  <div key={idx} className="flex items-center gap-2">
                    <input
                      type="datetime-local"
                      value={s}
                      onChange={(e) => setStep(idx, e.target.value)}
                      className="flex-1 text-sm text-text bg-bg border border-border rounded-lg px-3 py-2 outline-none focus:border-primary focus:ring-2 focus:ring-primary-100 transition-all"
                    />
                    <button
                      type="button"
                      onClick={() => removeStep(idx)}
                      disabled={steps.length === 1}
                      className="text-text-muted hover:text-danger text-xl leading-none px-2 cursor-pointer disabled:opacity-30 disabled:cursor-not-allowed"
                      aria-label="Odebrat termín"
                    >
                      ×
                    </button>
                  </div>
                ))}
              </div>
              <button
                type="button"
                onClick={addStep}
                className="mt-2 text-xs font-medium text-primary hover:underline cursor-pointer"
              >
                + Přidat termín
              </button>
              <p className="text-xs text-text-muted mt-2">
                Odpočet běží k nejbližšímu budoucímu termínu; jakmile jeden vyprší, automaticky
                naskočí další.
              </p>
            </div>
          )}

          {/* Design */}
          <div className="border-t border-border pt-4">
            <span className="text-sm font-medium text-text-secondary block mb-2">Design</span>
            <div className="grid grid-cols-2 gap-3">
              <label className="text-xs font-medium text-text-secondary">
                Font
                <select
                  value={design.font}
                  onChange={(e) =>
                    setDesignField('font', e.target.value as CountdownDesign['font'])
                  }
                  className="mt-1 w-full text-sm text-text bg-bg border border-border rounded-lg px-2.5 py-2 outline-none focus:border-primary cursor-pointer"
                >
                  {FONT_OPTIONS.map((f) => (
                    <option key={f.value} value={f.value}>
                      {f.label}
                    </option>
                  ))}
                </select>
              </label>
              <label className="text-xs font-medium text-text-secondary">
                Zaoblení rohů: {design.radius} px
                <input
                  type="range"
                  min={0}
                  max={40}
                  value={design.radius}
                  onChange={(e) => setDesignField('radius', Number(e.target.value))}
                  className="mt-1 w-full accent-primary cursor-pointer"
                />
              </label>
              <label className="text-xs font-medium text-text-secondary">
                Padding po stranách: {design.paddingX} px
                <input
                  type="range"
                  min={0}
                  max={200}
                  value={design.paddingX}
                  onChange={(e) => setDesignField('paddingX', Number(e.target.value))}
                  className="mt-1 w-full accent-primary cursor-pointer"
                />
              </label>
            </div>
            <label className="flex items-center gap-2 cursor-pointer select-none mt-3">
              <input
                type="checkbox"
                checked={design.transparentBg}
                onChange={(e) => setDesignField('transparentBg', e.target.checked)}
                className="w-4 h-4 rounded border-border accent-primary cursor-pointer"
              />
              <span className="text-xs font-medium text-text">Průhledné pozadí</span>
            </label>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-3">
              <ColorField
                label="Číslice"
                value={design.textColor}
                onChange={(v) => setDesignField('textColor', v)}
              />
              <ColorField
                label="Box"
                value={design.boxColor}
                onChange={(v) => setDesignField('boxColor', v)}
              />
              {!design.transparentBg && (
                <ColorField
                  label="Pozadí"
                  value={design.bgColor}
                  onChange={(v) => setDesignField('bgColor', v)}
                />
              )}
              <ColorField
                label="Popisky"
                value={design.labelColor}
                onChange={(v) => setDesignField('labelColor', v)}
              />
            </div>
          </div>

          {/* Živý náhled */}
          <div>
            <span className="text-sm font-medium text-text-secondary block mb-1.5">Náhled</span>
            {previewSrc ? (
              <img
                src={previewSrc}
                alt="Náhled odpočtu"
                className="w-full rounded-lg border border-border"
              />
            ) : (
              <div className="text-xs text-text-muted py-4 text-center bg-bg rounded-lg border border-border">
                Načítám náhled…
              </div>
            )}
            <p className="text-[11px] text-text-muted mt-1">
              Ukázkový čas 12 dní 8:45:30 — náhled se překreslí při každé změně designu.
            </p>
          </div>

          <div className="flex items-center justify-end gap-2 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="text-sm font-medium text-text-secondary hover:text-text bg-bg hover:bg-surface-hover border border-border rounded-lg px-4 py-2 cursor-pointer transition-colors"
            >
              Zrušit
            </button>
            <button
              type="submit"
              disabled={saving || !name.trim()}
              className="bg-action hover:bg-action-dark text-white text-sm font-semibold rounded-lg px-5 py-2 transition-colors cursor-pointer disabled:opacity-50"
            >
              {saving ? 'Ukládám…' : isEdit ? 'Uložit' : 'Vytvořit'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function ColorField({
  label,
  value,
  onChange,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
}) {
  const valid = /^#[0-9a-fA-F]{6}$/.test(value);
  return (
    <label className="text-xs font-medium text-text-secondary">
      {label}
      <div className="mt-1 flex items-center gap-1.5">
        <span
          className="w-7 h-7 rounded border border-border flex-shrink-0"
          style={{ backgroundColor: valid ? value : '#ffffff' }}
        />
        <input
          type="text"
          value={value}
          onChange={(e) => onChange(e.target.value.trim())}
          placeholder="#16243a"
          maxLength={7}
          spellCheck={false}
          className={`flex-1 min-w-0 text-sm bg-bg border rounded-lg px-2.5 py-1.5 outline-none focus:border-primary font-mono ${
            valid ? 'text-text border-border' : 'text-danger border-danger/40'
          }`}
        />
      </div>
    </label>
  );
}
